import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Switch,
  Select,
  message,
  Tabs,
  Spin,
} from "antd";
import { FaRobot } from "react-icons/fa";
import { RiSettings4Line } from "react-icons/ri";
import {
  FiExternalLink,
  FiSave,
  FiRefreshCw,
  FiSettings,
} from "react-icons/fi";
import { getAIConfig, updateAIConfig } from "@api/settings/aiConfig";

import "@assets/css/AiConfig.css";

const { Option } = Select;
const { TabPane } = Tabs;

// Model information with pricing (per 1M tokens, as of Dec 2025)
const MODEL_INFO = {
  openai: {
    "o3": { name: "o3", input: 10.00, output: 40.00, context: "200K", description: "Smartest reasoning model", recommended: true },
    "o4-mini": { name: "o4-mini", input: 1.10, output: 4.40, context: "200K", description: "Fast reasoning, best value" },
    "gpt-4.1": { name: "GPT-4.1", input: 2.00, output: 8.00, context: "1M", description: "Coding specialist, 1M context" },
    "gpt-4.1-mini": { name: "GPT-4.1 Mini", input: 0.40, output: 1.60, context: "1M", description: "Fast coding model" },
    "gpt-4o": { name: "GPT-4o", input: 2.50, output: 10.00, context: "128K", description: "Multimodal flagship" },
    "gpt-4o-mini": { name: "GPT-4o Mini", input: 0.15, output: 0.60, context: "128K", description: "Fast & affordable" },
    "o3-mini": { name: "o3 Mini", input: 1.10, output: 4.40, context: "200K", description: "Previous reasoning model" },
  },
  claude: {
    "claude-opus-4-5-20251124": { name: "Claude Opus 4.5", input: 15.00, output: 75.00, context: "200K", description: "Most intelligent", recommended: true },
    "claude-sonnet-4-5-20250929": { name: "Claude Sonnet 4.5", input: 3.00, output: 15.00, context: "200K", description: "Best for coding & agents" },
    "claude-opus-4-1-20250805": { name: "Claude Opus 4.1", input: 15.00, output: 75.00, context: "200K", description: "Enhanced code generation" },
    "claude-sonnet-4-20250514": { name: "Claude Sonnet 4", input: 3.00, output: 15.00, context: "200K", description: "Balanced performance" },
    "claude-3-5-haiku-20241022": { name: "Claude 3.5 Haiku", input: 0.80, output: 4.00, context: "200K", description: "Fast & efficient" },
  },
  deepseek: {
    "deepseek-chat": { name: "DeepSeek V3.2", input: 0.28, output: 0.42, context: "128K", description: "Latest general purpose", recommended: true },
    "deepseek-reasoner": { name: "DeepSeek R1", input: 0.28, output: 0.42, context: "128K", description: "Advanced reasoning, 32K CoT" },
  },
  gemini: {
    "gemini-2.5-pro": { name: "Gemini 2.5 Pro", input: 1.25, output: 5.00, context: "1M", description: "State-of-art thinking", recommended: true },
    "gemini-2.5-flash": { name: "Gemini 2.5 Flash", input: 0.15, output: 0.60, context: "1M", description: "Best price-performance" },
    "gemini-2.5-flash-lite": { name: "Gemini 2.5 Flash Lite", input: 0.075, output: 0.30, context: "1M", description: "Lowest cost & latency" },
    "gemini-2.0-flash": { name: "Gemini 2.0 Flash", input: 0.10, output: 0.40, context: "1M", description: "Previous fast model" },
  },
  groq: {
    "llama-3.3-70b-versatile": { name: "Llama 3.3 70B", input: 0.59, output: 0.79, context: "128K", description: "Best open model, 280 T/s", recommended: true },
    "meta-llama/llama-4-maverick-17b-128e-instruct": { name: "Llama 4 Maverick", input: 0.20, output: 0.20, context: "128K", description: "Latest Llama 4 multimodal" },
    "llama-3.1-8b-instant": { name: "Llama 3.1 8B", input: 0.05, output: 0.08, context: "128K", description: "Ultra fast, 560 T/s" },
    "qwen/qwen3-32b": { name: "Qwen3 32B", input: 0.20, output: 0.20, context: "128K", description: "Fast Qwen3, 400 T/s" },
  },
  qwen: {
    "qwen3-max": { name: "Qwen3 Max", input: 1.20, output: 6.00, context: "128K", description: "Latest flagship, SOTA agents", recommended: true },
    "qwen-max": { name: "Qwen Max", input: 1.60, output: 6.40, context: "32K", description: "Previous flagship" },
    "qwen-plus": { name: "Qwen Plus", input: 0.10, output: 0.30, context: "128K", description: "Balanced performance" },
    "qwen-turbo": { name: "Qwen Turbo", input: 0.02, output: 0.06, context: "128K", description: "Ultra affordable" },
    "qwen3-coder-plus": { name: "Qwen3 Coder Plus", input: 0.50, output: 2.00, context: "128K", description: "Code specialist" },
  },
  mistral: {
    "mistral-large-2512": { name: "Mistral Large 3", input: 2.00, output: 6.00, context: "256K", description: "675B MoE flagship", recommended: true },
    "mistral-medium-2505": { name: "Mistral Medium 3", input: 1.00, output: 3.00, context: "128K", description: "Frontier coding & STEM" },
    "mistral-small-2506": { name: "Mistral Small 3.2", input: 0.20, output: 0.60, context: "128K", description: "Fast & efficient" },
    "codestral-2501": { name: "Codestral", input: 0.30, output: 0.90, context: "256K", description: "Code completion" },
    "devstral-2-25-12": { name: "Devstral 2", input: 0.20, output: 0.60, context: "128K", description: "Code agents" },
  },
  together: {
    "meta-llama/Llama-4-Maverick-17B-128E-Instruct": { name: "Llama 4 Maverick", input: 0.27, output: 0.27, context: "128K", description: "Best Llama 4", recommended: true },
    "meta-llama/Llama-4-Scout-17B-16E-Instruct": { name: "Llama 4 Scout", input: 0.18, output: 0.18, context: "10M", description: "10M context window" },
    "deepseek-ai/DeepSeek-V3.1": { name: "DeepSeek V3.1", input: 0.55, output: 2.19, context: "128K", description: "Hybrid thinking" },
    "deepseek-ai/DeepSeek-R1-0528": { name: "DeepSeek R1", input: 3.00, output: 7.00, context: "64K", description: "87.5% AIME 2025" },
    "meta-llama/Llama-3.3-70B-Instruct-Turbo": { name: "Llama 3.3 70B Turbo", input: 0.88, output: 0.88, context: "128K", description: "Fast Llama 3.3" },
  },
  zai: {
    "glm-4.6": { name: "GLM-4.6", input: 0.60, output: 2.20, context: "200K", description: "Latest flagship, 200K context", recommended: true },
    "glm-4.5": { name: "GLM-4.5", input: 0.60, output: 2.20, context: "128K", description: "355B MoE, agentic AI" },
    "glm-4.5-air": { name: "GLM-4.5 Air", input: 0.20, output: 1.10, context: "128K", description: "Lightweight 106B model" },
    "glm-4.5-flash": { name: "GLM-4.5 Flash", input: 0.00, output: 0.00, context: "128K", description: "Free fast model" },
    "glm-4.6v-flash": { name: "GLM-4.6V Flash", input: 0.00, output: 0.00, context: "128K", description: "Free vision model" },
    "glm-4.5-x": { name: "GLM-4.5-X", input: 2.20, output: 8.90, context: "128K", description: "Extended premium model" },
  },
};

// Provider display info with API key console URLs
const PROVIDER_DISPLAY = {
  openai: { label: "OpenAI", icon: "🤖", color: "#10b981", apiKeyUrl: "https://platform.openai.com/api-keys" },
  claude: { label: "Claude", icon: "🎭", color: "#8b5cf6", apiKeyUrl: "https://console.anthropic.com/settings/keys" },
  deepseek: { label: "DeepSeek", icon: "🔮", color: "#3b82f6", apiKeyUrl: "https://platform.deepseek.com/api_keys" },
  gemini: { label: "Gemini", icon: "💎", color: "#4285f4", apiKeyUrl: "https://aistudio.google.com/apikey" },
  groq: { label: "Groq", icon: "⚡", color: "#f97316", apiKeyUrl: "https://console.groq.com/keys" },
  qwen: { label: "Qwen", icon: "🌟", color: "#ec4899", apiKeyUrl: "https://bailian.console.alibabacloud.com/" },
  mistral: { label: "Mistral", icon: "🌀", color: "#06b6d4", apiKeyUrl: "https://console.mistral.ai/api-keys" },
  together: { label: "Together", icon: "🤝", color: "#84cc16", apiKeyUrl: "https://api.together.xyz/settings/api-keys" },
  zai: { label: "Z.ai", icon: "🇨🇳", color: "#e11d48", apiKeyUrl: "https://z.ai/manage-apikey/apikey-list" },
};

// Feature display info for Copilotly
const FEATURE_DISPLAY = {
  // Server-side features
  blogGeneration: { label: "Blog Generation", icon: "📝", description: "AI-powered blog content generation for campaigns", category: "server" },
  // Extension features (streaming proxy)
  extensionChat: { label: "Extension Chat", icon: "💬", description: "AI chat functionality in browser extension", category: "extension" },
  extensionCraft: { label: "Extension Craft", icon: "✏️", description: "AI text transformation in browser extension", category: "extension" },
  extensionCopilot: { label: "Extension Copilot", icon: "🚀", description: "AI copilot with custom prompts in browser extension", category: "extension" },
};

// Helper to get models array for a provider
const getProviderModels = (provider) => Object.keys(MODEL_INFO[provider] || {});

// Helper to format price
const formatPrice = (price) => price < 1 ? `$${price.toFixed(2)}` : `$${price}`;

const AiConfig = () => {
  // AI Config State
  const [aiConfig, setAiConfig] = useState({
    features: {
      blogGeneration: { provider: "openai", model: "gpt-4o", enabled: true },
      extensionChat: { provider: "openai", model: "gpt-4o", enabled: true },
      extensionCraft: { provider: "openai", model: "gpt-4o", enabled: true },
      extensionCopilot: { provider: "openai", model: "gpt-4o", enabled: true },
    },
    apiKeys: {
      openai: false, claude: false, deepseek: false, gemini: false,
      groq: false, qwen: false, mistral: false, together: false, zai: false,
    },
  });
  const [aiLoading, setAiLoading] = useState(true);
  const [aiSaving, setAiSaving] = useState(false);
  const [activeFeatureTab, setActiveFeatureTab] = useState("blogGeneration");
  const [tempFeatures, setTempFeatures] = useState({
    blogGeneration: { provider: "openai", model: "gpt-4o", enabled: true },
    extensionChat: { provider: "openai", model: "gpt-4o", enabled: true },
    extensionCraft: { provider: "openai", model: "gpt-4o", enabled: true },
    extensionCopilot: { provider: "openai", model: "gpt-4o", enabled: true },
  });
  const [apiKeys, setApiKeys] = useState({
    openai: "", claude: "", deepseek: "", gemini: "",
    groq: "", qwen: "", mistral: "", together: "", zai: "",
  });
  const [showApiKeysSection, setShowApiKeysSection] = useState(true);
  const [hasAiChanges, setHasAiChanges] = useState(false);

  useEffect(() => {
    fetchAIConfig();
  }, []);

  // Track AI changes
  useEffect(() => {
    const featuresChanged = JSON.stringify(tempFeatures) !== JSON.stringify(aiConfig.features);
    const keysEntered = Object.values(apiKeys).some(key => key !== "");
    setHasAiChanges(featuresChanged || keysEntered);
  }, [tempFeatures, apiKeys, aiConfig.features]);

  // Fetch AI Config
  const fetchAIConfig = async () => {
    setAiLoading(true);
    try {
      const res = await getAIConfig();
      const config = {
        features: res.data.features || {
          blogGeneration: { provider: "openai", model: "gpt-4o", enabled: true },
          extensionChat: { provider: "openai", model: "gpt-4o", enabled: true },
          extensionCraft: { provider: "openai", model: "gpt-4o", enabled: true },
          extensionCopilot: { provider: "openai", model: "gpt-4o", enabled: true },
        },
        apiKeys: res.data.apiKeys || {},
      };
      setAiConfig(config);
      setTempFeatures(config.features);
    } catch (err) {
      console.error("Error fetching AI config", err);
      message.error("Failed to load AI configuration");
    } finally {
      setAiLoading(false);
    }
  };

  // Handle feature provider change
  const handleFeatureProviderChange = (featureName, provider) => {
    const models = getProviderModels(provider);
    const defaultModel = models[0];
    setTempFeatures((prev) => ({
      ...prev,
      [featureName]: { ...prev[featureName], provider, model: defaultModel },
    }));
  };

  // Handle feature model change
  const handleFeatureModelChange = (featureName, model) => {
    setTempFeatures((prev) => ({
      ...prev,
      [featureName]: { ...prev[featureName], model },
    }));
  };

  // Handle feature enabled toggle
  const handleFeatureEnabledChange = (featureName, enabled) => {
    setTempFeatures((prev) => ({
      ...prev,
      [featureName]: { ...prev[featureName], enabled },
    }));
  };

  // Save AI Config
  const handleSaveAiConfig = async () => {
    setAiSaving(true);
    try {
      const payload = {
        features: tempFeatures,
        apiKeys: {},
      };
      // Only include API keys if they were entered (non-empty)
      Object.keys(apiKeys).forEach((provider) => {
        if (apiKeys[provider]) {
          payload.apiKeys[provider] = apiKeys[provider];
        }
      });

      const res = await updateAIConfig(payload);
      setAiConfig({
        features: res.data.features,
        apiKeys: res.data.apiKeys,
      });
      // Clear entered API keys after save
      setApiKeys({
        openai: "", claude: "", deepseek: "", gemini: "",
        groq: "", qwen: "", mistral: "", together: "", zai: "",
      });
      message.success("AI configuration saved successfully");
    } catch (err) {
      console.error("Error updating AI config", err);
      message.error("Failed to save AI configuration");
    } finally {
      setAiSaving(false);
    }
  };

  // Reset AI Config to saved values
  const handleAiReset = () => {
    setTempFeatures({ ...aiConfig.features });
    setApiKeys({
      openai: "", claude: "", deepseek: "", gemini: "",
      groq: "", qwen: "", mistral: "", together: "", zai: "",
    });
    message.info("Configuration reset to last saved state");
  };

  // Render loading state
  const renderLoading = (text) => (
    <div className="ai-config-loading">
      <Spin size="large" />
      <p>{text}</p>
    </div>
  );

  if (aiLoading) {
    return (
      <div className="ai-config-page settings-page">
        {renderLoading("Loading AI Configuration...")}
      </div>
    );
  }

  return (
    <div className="ai-config-page settings-page">
      {/* Header Bar */}
      <div className="settings-header-bar">
        <div className="settings-header-left">
          <div className="settings-header-icon">
            <FiSettings size={20} />
          </div>
          <h1>AI Configuration</h1>
        </div>

        <div className="settings-header-actions">
          <Button
            icon={<FiRefreshCw size={14} />}
            onClick={handleAiReset}
            disabled={!hasAiChanges}
            className="settings-btn-reset"
          >
            Reset
          </Button>
          <Button
            type="primary"
            icon={<FiSave size={14} />}
            onClick={handleSaveAiConfig}
            loading={aiSaving}
            disabled={!hasAiChanges}
            className="settings-btn-save"
          >
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="settings-content">
        {/* Feature Configuration Card */}
        <Card className="ai-config-card" bordered={false}>
          <div className="ai-config-card-header">
            <h2>Feature Settings</h2>
            <p>Configure different AI providers for each feature independently</p>
          </div>

          <Tabs
            activeKey={activeFeatureTab}
            onChange={setActiveFeatureTab}
            tabPosition="left"
            className="ai-config-tabs"
          >
            {Object.entries(FEATURE_DISPLAY).map(([featureKey, featureInfo]) => (
              <TabPane
                tab={
                  <div className="ai-config-tab-label">
                    <span className="ai-config-tab-icon">{featureInfo.icon}</span>
                    <span className="ai-config-tab-text">{featureInfo.label}</span>
                    {tempFeatures[featureKey]?.enabled === false && (
                      <span className="ai-config-tab-disabled">(off)</span>
                    )}
                  </div>
                }
                key={featureKey}
              >
                <div className="ai-config-feature-panel">
                  {/* Feature Enable/Disable */}
                  <div className="ai-config-feature-toggle">
                    <div className="ai-config-feature-toggle-info">
                      <h3>{featureInfo.label}</h3>
                      <p>{featureInfo.description}</p>
                    </div>
                    <Switch
                      checked={tempFeatures[featureKey]?.enabled !== false}
                      onChange={(checked) => handleFeatureEnabledChange(featureKey, checked)}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                  </div>

                  {tempFeatures[featureKey]?.enabled !== false && (
                    <>
                      {/* Provider Selection */}
                      <div className="ai-config-section">
                        <label className="ai-config-label">AI Provider</label>
                        <div className="ai-config-provider-grid">
                          {Object.entries(PROVIDER_DISPLAY).map(([providerKey, providerInfo]) => (
                            <div
                              key={providerKey}
                              onClick={() => handleFeatureProviderChange(featureKey, providerKey)}
                              className={`ai-config-provider-card ${tempFeatures[featureKey]?.provider === providerKey ? 'selected' : ''}`}
                              style={{
                                '--provider-color': providerInfo.color,
                              }}
                            >
                              {aiConfig.apiKeys?.[providerKey] && (
                                <div className="ai-config-provider-status" />
                              )}
                              <div className="ai-config-provider-icon">{providerInfo.icon}</div>
                              <div className="ai-config-provider-label">{providerInfo.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Model Selection */}
                      <div className="ai-config-section">
                        <label className="ai-config-label">Model</label>
                        <Select
                          value={tempFeatures[featureKey]?.model}
                          onChange={(model) => handleFeatureModelChange(featureKey, model)}
                          className="ai-config-select"
                          size="large"
                        >
                          {getProviderModels(tempFeatures[featureKey]?.provider || "openai").map((modelId) => {
                            const info = MODEL_INFO[tempFeatures[featureKey]?.provider || "openai"]?.[modelId];
                            if (!info) return null;
                            return (
                              <Option key={modelId} value={modelId}>
                                <div className="ai-config-model-option">
                                  <div className="ai-config-model-name">
                                    <span>{info.name}</span>
                                    {info.recommended && (
                                      <span className="ai-config-model-badge">BEST</span>
                                    )}
                                  </div>
                                  <span className="ai-config-model-price">
                                    {formatPrice(info.input)}/{formatPrice(info.output)} • {info.context}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}
                        </Select>

                        {/* Model Description */}
                        {tempFeatures[featureKey]?.model && MODEL_INFO[tempFeatures[featureKey]?.provider]?.[tempFeatures[featureKey]?.model] && (
                          <div className="ai-config-model-description">
                            {MODEL_INFO[tempFeatures[featureKey]?.provider][tempFeatures[featureKey]?.model].description}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </TabPane>
            ))}
          </Tabs>
        </Card>

        {/* API Keys Card */}
        <Card className="ai-config-card" bordered={false}>
          <div
            className="ai-config-card-header clickable"
            onClick={() => setShowApiKeysSection(!showApiKeysSection)}
          >
            <div className="ai-config-card-header-left">
              <RiSettings4Line size={20} />
              <div>
                <h2>API Keys</h2>
                <p>{Object.values(aiConfig.apiKeys || {}).filter(Boolean).length} of 9 providers configured</p>
              </div>
            </div>
            <span className={`ai-config-collapse-icon ${showApiKeysSection ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>

          {showApiKeysSection && (
            <div className="ai-config-api-keys-grid">
              {Object.entries(PROVIDER_DISPLAY).map(([providerKey, providerInfo]) => (
                <div key={providerKey} className="ai-config-api-key-item">
                  <div className="ai-config-api-key-header">
                    <div className="ai-config-api-key-info">
                      <span className="ai-config-api-key-icon">{providerInfo.icon}</span>
                      <span className="ai-config-api-key-label">{providerInfo.label}</span>
                      {aiConfig.apiKeys?.[providerKey] && (
                        <span className="ai-config-api-key-badge">SET</span>
                      )}
                    </div>
                    <a
                      href={providerInfo.apiKeyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ai-config-api-key-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Get Key <FiExternalLink size={10} />
                    </a>
                  </div>
                  <Input.Password
                    placeholder={aiConfig.apiKeys?.[providerKey] ? "••••••••" : "Enter API key..."}
                    value={apiKeys[providerKey]}
                    onChange={(e) => setApiKeys(prev => ({ ...prev, [providerKey]: e.target.value }))}
                    className="ai-config-api-key-input"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Info Banner */}
          <div className="ai-config-info-banner">
            <span className="ai-config-info-icon">i</span>
            <p>
              Configure different AI providers for each feature. API keys are encrypted and stored securely.
              If not set, falls back to server environment variables.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AiConfig;
